import React from "react";
import * as io from "io-ts";
import createGlobalState from "../../lib/createGlobalState";
import type Highcharts from "highcharts";
import { isFuncState } from "../../lib/isFuncState";

const useHiddenSeriesHook = createGlobalState({
  storage: "session",
  key: "hiddenSeries",
  type: io.array(io.string),
  scoped: true,
});

export default function useVisibleSeries<T extends Highcharts.SeriesOptionsType>(
  chartId: string,
  series: T[],
  selectedOptions?: string[],
) {
  const [state, setState] = useHiddenSeriesHook({
    scope: chartId,
    defaultValue: [],
  });

  if (series.some(s => !s.id)) throw new Error("Missing serie id");
  if (series.length !== [...new Set(series.map(s => s.id))].length)
    throw new Error("Serie ids are not unique");

  const serieIds = React.useMemo(() => series.map(s => s.id!), [series]);

  const visibleSeries = React.useMemo(
    () =>
      series.map(serie => {
        const selected = selectedOptions ? selectedOptions.includes(serie.id!) : true;

        return {
          ...serie,
          visible: selectedOptions
            ? !state.includes(serie.id!) && selected
            : !state.includes(serie.id!),
          selected,
        };
      }),
    [series, selectedOptions, state],
  );

  const onSerieClick = React.useCallback(
    (clickedSerieId: string, altClick: boolean) => {
      setState(hiddenSerieIds => {
        let newHiddenSerieIds = hiddenSerieIds.filter(id => serieIds.includes(id)); // in case series changed, remove dead data

        // helper functions
        const hideSerie = (serieId: string) => {
          if (!newHiddenSerieIds.includes(serieId))
            newHiddenSerieIds = [...newHiddenSerieIds, serieId];
        };
        const showSerie = (serieId: string) => {
          newHiddenSerieIds = newHiddenSerieIds.filter(id => serieId !== id);
        };
        const toggleSerie = (serieId: string) => {
          if (newHiddenSerieIds.includes(serieId)) showSerie(serieId);
          else hideSerie(serieId);
        };

        if (altClick) {
          // simply toggle clicked serie
          toggleSerie(clickedSerieId);
        } else {
          // special sauce: toggle others, show clicked
          const otherSeries = series.filter(s => s.id! !== clickedSerieId);
          const allOthersInvisible = otherSeries.every(s => newHiddenSerieIds.includes(s.id!));

          if (allOthersInvisible) for (const s of otherSeries) showSerie(s.id!);
          else for (const s of otherSeries) hideSerie(s.id!);
          showSerie(clickedSerieId);
        }

        return newHiddenSerieIds;
      });
    },
    [setState, serieIds, series],
  );

  const setSerieVisibility = React.useCallback(
    (serieId: string, value: React.SetStateAction<boolean>) => {
      setState(hiddenSerieIds => {
        const isHidden = hiddenSerieIds.includes(serieId);
        const willBeVisible = isFuncState(value) ? value(!isHidden) : value;

        if (isHidden && willBeVisible) {
          return hiddenSerieIds.filter(s => s !== serieId);
        } else if (!isHidden && !willBeVisible) {
          return [...hiddenSerieIds, serieId];
        }
        return hiddenSerieIds;
      });
    },
    [setState],
  );

  return [visibleSeries, onSerieClick, setSerieVisibility] as const;
}
